import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { ref, onMounted, onBeforeUnmount, watch } from "vue";
	import L from "leaflet";
	import "leaflet/dist/leaflet.css";
	import api from "@/api";
	import { getEcho } from "@/services/echo";
	import { enterpriseID } from "@/state/api/userState";

	const STALE_AFTER_MS = 2 * 60 * 1000;

	const MAX_TRAIL_POINTS = 1500;

	
export default /*#__PURE__*/_defineComponent({
  __name: 'LiveFleetMap',
  props: {
    focusVehicleId: {}
  },
  emits: ["vehicle-clicked"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

	/**
	 * Live fleet map. Loads the current snapshot of every vehicle
	 * via /tracking/fleet, then subscribes to the
	 * `private-enterprise.{id}.fleet` Echo channel and animates
	 * markers as new positions arrive.
	 *
	 * Markers carry a CSS-rotated triangle so heading is visible at
	 * a glance. Online (last position < 2 min) markers are green,
	 * stale ones grey.
	 */
	const props = __props;
	const emit = __emit;

	const mapEl = ref<HTMLElement | null>(null);
	let map: L.Map | null = null;
	let echoChannel: any = null;
	const markers = new Map<number, L.Marker>();
	const labels = new Map<number, string>();
	// Driver info is only delivered by the snapshot fetch (/tracking/fleet) — Echo
	// position events stay lean and don't carry it. We cache it per-vehicle so the
	// tooltip remains accurate when a position update arrives between snapshots.
	const driverInfo = new Map<number, { name: string | null; phone: string | null }>();

	// P4 — Travel route metadata par véhicule (sert à charger les stops
	// pour l'overlay polyline). Alimenté par /tracking/fleet, jamais par
	// Echo (les events position ne portent pas le travel_route).
	const routeInfo = new Map<number, { uuid: string | null; label: string | null }>();

	// P4 — Layer Leaflet de l'overlay "trajet planifié" du véhicule
	// actuellement focusé. Une seule overlay active à la fois : on clear
	// avant de tracer la nouvelle pour éviter l'empilement.
	let plannedRouteLayer: L.LayerGroup | null = null;
	let currentRouteVehicleId: number | null = null;

	// Cache géométries OSRM par route UUID (clé) → array de [lat,lng].
	// OSRM public a un rate limit ; on garde les résultats pendant la
	// durée de vie du composant pour les re-focus successifs sur le
	// même véhicule/route.
	const osrmGeometryCache = new Map<string, Array<[number, number]>>();

	// Cache géocodage Nominatim par label normalisé (clé) → {lat,lng}|null.
	// Beaucoup de stops sont créés sans coords explicites — on retombe
	// sur le label de la ville (Yaoundé, Bafia, Foumban…) pour quand même
	// tracer un itinéraire approximatif. Cache process-wide pour éviter
	// de spammer l'API publique de Nominatim (1 req/s max).
	const geocodeCache = new Map<string, { lat: number; lng: number } | null>();

	// Fallback offline pour les villes Cameroun les plus communes.
	// Évite un round-trip Nominatim (1 req/s rate limit + ~300 ms latence)
	// quand l'opérateur a juste tapé "Bafia" sans coords. Surtout, c'est
	// notre filet de sécurité si Nominatim est down/bloqué/rate-limité :
	// sans ça, tous les stops sans coords étaient géocodés en parallèle
	// au boot et le moindre échec faisait disparaître la polyline (filter
	// final < 2 points). Clé = label lowercase trimé (même normalisation
	// que geocodeCache pour cohabiter).
	const CAMEROUN_CITY_COORDS: Record<string, { lat: number; lng: number }> = {
		"yaoundé": { lat: 3.866667, lng: 11.516667 },
		"yaounde": { lat: 3.866667, lng: 11.516667 },
		"douala": { lat: 4.0511, lng: 9.7679 },
		"bafia": { lat: 4.7399, lng: 11.2206 },
		"foumbot": { lat: 5.5109, lng: 10.6381 },
		"foumban": { lat: 5.7167, lng: 10.9 },
		"koutaba": { lat: 5.6511, lng: 10.7609 },
		"bafoussam": { lat: 5.4781, lng: 10.4167 },
		"bamenda": { lat: 5.9597, lng: 10.1463 },
		"garoua": { lat: 9.3017, lng: 13.3921 },
		"maroua": { lat: 10.5949, lng: 14.3253 },
		"ngaoundéré": { lat: 7.3214, lng: 13.5847 },
		"ngaoundere": { lat: 7.3214, lng: 13.5847 },
		"ebolowa": { lat: 2.9, lng: 11.15 },
		"bertoua": { lat: 4.5772, lng: 13.6843 },
		"kribi": { lat: 2.95, lng: 9.9 },
		"limbé": { lat: 4.0167, lng: 9.2167 },
		"limbe": { lat: 4.0167, lng: 9.2167 },
		"buea": { lat: 4.155, lng: 9.231 },
	};

	const geocodeLabel = async (
		label: string,
		countryHint = "Cameroun",
	): Promise<{ lat: number; lng: number } | null> => {
		const key = label.trim().toLowerCase();
		if (geocodeCache.has(key)) return geocodeCache.get(key) ?? null;
		// 1) Lookup hardcodé : instantané, robuste hors-ligne, immunisé au
		// rate-limit Nominatim. On enregistre aussi dans geocodeCache pour
		// que les prochains appels passent par le même chemin court.
		const hardcoded = CAMEROUN_CITY_COORDS[key];
		if (hardcoded) {
			geocodeCache.set(key, hardcoded);
			return hardcoded;
		}
		// 2) Sinon, Nominatim (best-effort, peut échouer silencieusement).
		const query = encodeURIComponent(`${label}, ${countryHint}`);
		const url = `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`;
		try {
			const ctrl = new AbortController();
			const timeout = setTimeout(() => ctrl.abort(), 6000);
			const res = await fetch(url, {
				signal: ctrl.signal,
				// Nominatim exige un User-Agent identifiant l'app.
				headers: { Accept: "application/json" },
			});
			clearTimeout(timeout);
			if (!res.ok) {
				geocodeCache.set(key, null);
				return null;
			}
			const data: any[] = await res.json();
			if (!Array.isArray(data) || data.length === 0) {
				geocodeCache.set(key, null);
				return null;
			}
			const hit = data[0];
			const lat = Number(hit.lat);
			const lng = Number(hit.lon);
			if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
				geocodeCache.set(key, null);
				return null;
			}
			const r = { lat, lng };
			geocodeCache.set(key, r);
			return r;
		} catch (e) {
			console.warn("[Nominatim] geocode failed for", label, e);
			geocodeCache.set(key, null);
			return null;
		}
	};

	// Récupère la géométrie routière réelle entre une suite de points via
	// l'API publique OSRM. Retourne un array de [lat,lng] ou null si
	// l'API échoue (réseau, rate limit, points hors couverture). Le
	// fallback ligne droite est appliqué par l'appelant.
	const fetchOsrmRoute = async (
		routeKey: string,
		points: Array<{ lat: number; lng: number }>,
	): Promise<Array<[number, number]> | null> => {
		const cached = osrmGeometryCache.get(routeKey);
		if (cached) return cached;
		// OSRM attend lng,lat (pas lat,lng) — inverse classique.
		const coords = points.map((p) => `${p.lng},${p.lat}`).join(';');
		const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;
		try {
			const ctrl = new AbortController();
			const timeout = setTimeout(() => ctrl.abort(), 8000);
			const res = await fetch(url, { signal: ctrl.signal });
			clearTimeout(timeout);
			if (!res.ok) return null;
			const data: any = await res.json();
			const geo = data?.routes?.[0]?.geometry?.coordinates;
			if (!Array.isArray(geo) || geo.length === 0) return null;
			// GeoJSON [lng,lat] → Leaflet [lat,lng]
			const latlng = geo.map((c: number[]) => [c[1], c[0]] as [number, number]);
			osrmGeometryCache.set(routeKey, latlng);
			return latlng;
		} catch (e) {
			console.warn('[OSRM] fallback ligne droite, fetch route échoué', e);
			return null;
		}
	};

	const buildIcon = (heading: number | null, online: boolean): L.DivIcon => {
		const rot = heading ?? 0;
		const color = online ? '#16a34a' : '#6b7280';
		const html = `
			<div style="transform: rotate(${rot}deg); width: 28px; height: 28px;">
				<svg viewBox="0 0 24 24" width="28" height="28" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.4));">
					<polygon points="12,2 20,22 12,17 4,22" fill="${color}" stroke="#fff" stroke-width="1.5"/>
				</svg>
			</div>`;
		return L.divIcon({ html, className: 'fleet-marker', iconSize: [28, 28], iconAnchor: [14, 14] });
	};

	const isOnline = (iso: string | null | undefined): boolean => {
		if (!iso) return false;
		return Date.now() - new Date(iso).getTime() < STALE_AFTER_MS;
	};

	const escapeHtml = (s: string): string =>
		s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

	const upsertMarker = (v: any) => {
		if (v.lat == null || v.lng == null) return;
		const online = isOnline(v.last_position_at || v.recorded_at);
		const icon = buildIcon(v.heading ?? null, online);

		const id = v.vehicle_id ?? v.id;
		labels.set(id, v.matriculation || v.label || '#' + id);

		// Refresh driver cache when the row carries it (snapshot fetch).
		// Echo events don't have driver_name/phone — leave the cache alone.
		if (v.driver_name !== undefined || v.driver_phone !== undefined) {
			driverInfo.set(id, {
				name: v.driver_name ?? null,
				phone: v.driver_phone ?? null,
			});
		}
		// P4 — même règle pour le travel_route : seul le snapshot l'apporte.
		if (v.travel_route_uuid !== undefined || v.travel_route_label !== undefined) {
			routeInfo.set(id, {
				uuid: v.travel_route_uuid ?? null,
				label: v.travel_route_label ?? null,
			});
		}
		const driver = driverInfo.get(id) || { name: null, phone: null };

		const matri = escapeHtml(String(v.matriculation || v.label || '#' + id));
		const driverLine = driver.name
			? `<div style="margin-top:4px"><span style="color:#9ca3af">Chauffeur :</span> ${escapeHtml(driver.name)}</div>`
			: `<div style="margin-top:4px;color:#9ca3af;font-style:italic">Chauffeur non assigné</div>`;
		const phoneLine = driver.phone
			? `<div><span style="color:#9ca3af">Tél :</span> <a href="tel:${escapeHtml(driver.phone)}" style="color:#60a5fa;text-decoration:none">${escapeHtml(driver.phone)}</a></div>`
			: '';
		const speedLine = v.speed != null ? `<div><span style="color:#9ca3af">Vitesse :</span> ${Number(v.speed).toFixed(1)} km/h</div>` : '';
		const seenLine = v.last_position_at
			? `<div style="color:#6b7280;font-size:11px;margin-top:4px">Vu à ${new Date(v.last_position_at).toLocaleTimeString('fr-FR')}</div>`
			: '';
		const statusDot = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${online ? '#16a34a' : '#6b7280'};margin-right:6px"></span>`;
		const tooltipHtml = `
			<div style="min-width:180px">
				<div style="display:flex;align-items:center;font-weight:600;color:#fff">
					${statusDot}${matri}
				</div>
				${driverLine}
				${phoneLine}
				${speedLine}
				${seenLine}
			</div>`;
		// Click popup keeps the same content as hover tooltip — single
		// source of truth, plus phone is clickable on touch where hover
		// tooltips don't trigger.
		const popup = tooltipHtml;

		const wasNew = !markers.has(id);
		const existing = markers.get(id);
		if (existing) {
			existing.setLatLng([v.lat, v.lng]);
			existing.setIcon(icon);
			existing.setPopupContent(popup);
			existing.setTooltipContent(tooltipHtml);
		} else if (map) {
			const m = L.marker([v.lat, v.lng], { icon }).addTo(map);
			m.bindPopup(popup);
			m.bindTooltip(tooltipHtml, {
				direction: 'top',
				offset: [0, -14],
				opacity: 0.95,
				className: 'fleet-tooltip',
				sticky: true,
			});
			m.on('click', () => emit('vehicle-clicked', id));
			markers.set(id, m);
		}

		// Re-fit only when a brand-new marker arrives outside the current
		// viewport (typical case: driver starts sharing après le chargement
		// de page — initial fetch was empty so no fitBounds ran, and the
		// Echo event drops a marker far from the default Cameroun view).
		// Existing markers moving stay put — refitting on every position
		// update would jerk the map every second.
		//
		// Exception : si le payload porte un travel_route_uuid, l'auto-draw
		// du tracé planifié va appeler son propre fitBounds (qui inclut le
		// véhicule si proche, le trajet seul sinon). On évite donc le
		// flash "zoom GPS véhicule → zoom tracé" en sautant ce fitToMarkers.
		if (wasNew && map && !v.travel_route_uuid) {
			const bounds = map.getBounds();
			if (!bounds.contains([v.lat, v.lng])) {
				fitToMarkers();
			}
		}
	};

	const fitToMarkers = () => {
		if (!map || markers.size === 0) return;
		const group = L.featureGroup(Array.from(markers.values()));
		map.fitBounds(group.getBounds().pad(0.2));
	};

	// P4 — clear l'overlay "trajet planifié" courant. Appelé avant de
	// dessiner un autre overlay (changement de véhicule focusé) ou au
	// démontage du composant.
	const clearPlannedRoute = () => {
		if (plannedRouteLayer) {
			plannedRouteLayer.remove();
			plannedRouteLayer = null;
		}
		currentRouteVehicleId = null;
	};

	// P4 — récupère les stops du travel_route lié au véhicule et trace
	// polyline + markers numérotés. Le marker GPS du véhicule reste géré
	// par upsertMarker (couche distincte). Si le trajet n'a pas de stops
	// configurés (< 2), on n'affiche rien — pas d'erreur, c'est le
	// comportement historique.
	const drawPlannedRouteForVehicle = async (vehicleId: number) => {
		console.log("[P4] drawPlannedRouteForVehicle entered", { vehicleId });
		if (!map) {
			console.warn("[P4] no map yet, abort", { vehicleId });
			return;
		}
		// Idempotent : si l'overlay déjà tracé concerne ce véhicule, no-op.
		if (currentRouteVehicleId === vehicleId && plannedRouteLayer) {
			console.log("[P4] already drawn for this vehicle, skip", { vehicleId });
			return;
		}
		clearPlannedRoute();
		// Mémorise la cible AVANT le clear pour anti-race : si l'utilisateur
		// re-clique sur un autre véhicule pendant le fetch async, on doit
		// abandonner l'écriture sur la carte (sinon overlay du vehicleA
		// dessiné par-dessus celui du vehicleB sélectionné après).
		currentRouteVehicleId = vehicleId;
		const route = routeInfo.get(vehicleId);
		console.log("[P4] routeInfo lookup", { vehicleId, route });
		if (!route?.uuid) {
			console.warn("[P4] no route.uuid, abort", { vehicleId, route });
			return;
		}
		try {
			const res: any = await api().routes.stops.list(route.uuid);
			// Garde anti-race : si une autre cible a été focusée pendant le
			// fetch, on jette le résultat (le clear suivant aurait nettoyé
			// notre layer de toute façon, mais autant ne pas le dessiner).
			if (currentRouteVehicleId !== vehicleId) {
				console.log("[P4] race lost after stops fetch", { vehicleId });
				return;
			}
			const stops = Array.isArray(res.data) ? res.data : res.data?.data || [];
			console.log("[P4] stops fetched", { vehicleId, count: stops.length });
			// Étape 1 : sortir par sequence et hydrater depuis les coords
			// disponibles (effective_lat/lng = stop direct OU station liée).
			const sorted = [...stops].sort((a: any, b: any) => a.sequence - b.sequence);
			const hydrated = sorted.map((s: any) => ({
				raw: s,
				lat: s.effective_latitude ?? s.latitude ?? null,
				lng: s.effective_longitude ?? s.longitude ?? null,
				label: s.display_label || s.label || `Stop #${s.sequence}`,
			}));

			// Étape 2 : pour les stops sans coords, fallback géocodage Nominatim
			// sur le label. Beaucoup d'admins créent les stops sans saisir
			// lat/lng — au lieu de ne rien afficher, on tente une résolution
			// best-effort sur les villes connues (Yaoundé, Bafia, Foumban…).
			for (const h of hydrated) {
				if (h.lat == null || h.lng == null) {
					const geo = await geocodeLabel(h.label);
					if (currentRouteVehicleId !== vehicleId) return; // anti-race après await
					if (geo) {
						h.lat = geo.lat;
						h.lng = geo.lng;
					}
				}
			}

			// Étape 3 : filtre final + projection au format attendu.
			type RoutePoint = {
				lat: number;
				lng: number;
				seq: number;
				label: string;
				kind: "origin" | "terminus" | "intermediate";
			};
			const stopPoints: RoutePoint[] = hydrated
				.filter((p: any) => p.lat != null && p.lng != null)
				.map((p: any) => ({
					lat: Number(p.lat),
					lng: Number(p.lng),
					seq: p.raw.sequence,
					label: p.label,
					// is_terminus du stop = on en fait la destination finale.
					// Origin sera ajouté plus bas si pas déjà présent (sequence 1).
					kind: p.raw.is_terminus ? "terminus" : "intermediate",
				}));

			// Étape 3 bis : ajout des AGENCES origine/terminus dérivées du
			// label du travel_route (typiquement "Yaoundé - koutaba"). Si
			// l'admin n'a pas créé ces agences comme stops explicites, on
			// les ajoute quand même pour que le tracé démarre/finisse à
			// l'agence — exactement comme sur l'illustration de référence
			// (pin vert départ + pin rouge arrivée).
			const routeLabel = (route.label || "").trim();
			const labelParts = routeLabel.split(/\s*-\s*/).map((s: string) => s.trim()).filter(Boolean);
			const originName = labelParts[0] || "";
			const terminusName = labelParts[labelParts.length - 1] || "";
			const norm = (s: string) => s.trim().toLowerCase();
			const hasOrigin = stopPoints.some((p) => norm(p.label) === norm(originName));
			const hasTerminus = stopPoints.some((p) => norm(p.label) === norm(terminusName));
			if (originName && !hasOrigin) {
				const geo = await geocodeLabel(originName);
				if (currentRouteVehicleId !== vehicleId) return;
				if (geo) {
					stopPoints.unshift({
						lat: geo.lat,
						lng: geo.lng,
						seq: 0,
						label: originName,
						kind: "origin",
					});
				}
			} else if (hasOrigin) {
				// L'agence d'origine EST un stop explicite : on requalifie
				// le premier en "origin" pour le rendu pin vert.
				const firstIdx = stopPoints.findIndex((p) => norm(p.label) === norm(originName));
				if (firstIdx >= 0 && stopPoints[firstIdx].kind !== "terminus") {
					stopPoints[firstIdx].kind = "origin";
				}
			}
			if (terminusName && !hasTerminus) {
				const geo = await geocodeLabel(terminusName);
				if (currentRouteVehicleId !== vehicleId) return;
				if (geo) {
					stopPoints.push({
						lat: geo.lat,
						lng: geo.lng,
						seq: 999,
						label: terminusName,
						kind: "terminus",
					});
				}
			} else if (hasTerminus) {
				// Le terminus EST un stop explicite (cas typique : admin a
				// créé "koutaba" comme stop mais sans cocher la case
				// is_terminus). On le requalifie pour afficher le pin rouge
				// "arrivée" au lieu d'une pastille orange numérotée.
				// On cherche depuis la fin pour cibler le DERNIER occurrence
				// (au cas où le nom apparaît plusieurs fois — improbable mais
				// safe).
				for (let i = stopPoints.length - 1; i >= 0; i--) {
					if (norm(stopPoints[i].label) === norm(terminusName)) {
						if (stopPoints[i].kind !== "origin") {
							stopPoints[i].kind = "terminus";
						}
						break;
					}
				}
			}

			console.log("[P4] stops geocoded", {
				vehicleId,
				total: hydrated.length,
				resolved: stopPoints.length,
				origin: originName,
				terminus: terminusName,
				labels: stopPoints.map((p) => ({ label: p.label, kind: p.kind })),
			});
			if (stopPoints.length < 2) {
				console.warn("[P4] less than 2 geocoded stops, polyline skipped", { vehicleId, count: stopPoints.length });
				return;
			}

			// Position GPS courante du véhicule : injectée en tête des
			// waypoints OSRM pour que le tracé routier parte de "où est
			// le bus maintenant" et passe par tous les stops planifiés.
			// MAIS : si la position véhicule est aberrante (test GPS sur un
			// autre continent, ou stale > horizon raisonnable du trajet),
			// on l'EXCLUT pour ne pas faire exploser le zoom et tracer
			// 5000 km à travers Sahara. Seuil 500 km : dépasse largement le
			// plus long trajet Cameroun (Douala-Maroua ~1300 km routier, ~900
			// km à vol d'oiseau) tout en filtrant les positions
			// franchement hors-zone.
			const vehicleMarker = markers.get(vehicleId);
			const vehicleLatLng = vehicleMarker ? vehicleMarker.getLatLng() : null;
			const vehiclePointRaw = vehicleLatLng
				? { lat: vehicleLatLng.lat, lng: vehicleLatLng.lng }
				: null;

			const MAX_VEHICLE_DIST_M = 500_000; // 500 km
			const haversine = (
				a: { lat: number; lng: number },
				b: { lat: number; lng: number },
			): number => {
				const R = 6371000;
				const dLat = ((b.lat - a.lat) * Math.PI) / 180;
				const dLng = ((b.lng - a.lng) * Math.PI) / 180;
				const lat1 = (a.lat * Math.PI) / 180;
				const lat2 = (b.lat * Math.PI) / 180;
				const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
				return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
			};
			let vehicleInTracé = false;
			if (vehiclePointRaw) {
				const minDist = Math.min(...stopPoints.map((sp) => haversine(vehiclePointRaw, sp)));
				vehicleInTracé = minDist <= MAX_VEHICLE_DIST_M;
				if (!vehicleInTracé) {
					console.warn("[P4] vehicle GPS far outside trajet area, excluded from route", {
						vehicleId,
						vehiclePos: vehiclePointRaw,
						minDistKm: Math.round(minDist / 1000),
					});
				}
			}
			const vehiclePoint = vehicleInTracé ? vehiclePointRaw : null;

			// Liste de waypoints transmise à OSRM/fallback.
			const routePoints: Array<{ lat: number; lng: number }> = vehiclePoint
				? [vehiclePoint, ...stopPoints]
				: stopPoints;

			// Le cache OSRM doit inclure la position courante du véhicule
			// (sinon on cache l'ancien tracé partant d'une vieille position).
			// On arrondit à 3 décimales (~100 m) pour ne pas invalider à
			// chaque micro-mouvement GPS — la géométrie OSRM ne change pas
			// significativement à cette précision.
			const cacheKey = vehiclePoint
				? `${route.uuid}|${vehiclePoint.lat.toFixed(3)},${vehiclePoint.lng.toFixed(3)}`
				: route.uuid;

			// FeatureGroup (pas LayerGroup) : FeatureGroup expose getBounds()
			// qui itère ses enfants et compose un bounding box correct sur
			// les polylines + markers. LayerGroup, lui, n'a pas getBounds —
			// du coup le `L.featureGroup([group, vehicleMarker]).getBounds()`
			// renvoyait un bounds invalide et le fitBounds final était sauté
			// silencieusement (carte restait sur la vue par défaut Cameroun,
			// donnant l'impression que la polyline n'avait pas été tracée
			// alors qu'elle l'était mais hors viewport).
			const group = L.featureGroup();
			// Tracé : on demande à OSRM la géométrie routière réelle entre
			// la position véhicule et les stops (suit les routes vs ligne
			// droite à vol d'oiseau). Si OSRM échoue, fallback ligne droite
			// pour ne pas casser l'UX si le serveur public est indisponible.
			const straight = routePoints.map((p) => [p.lat, p.lng] as [number, number]);
			const osrmPath = await fetchOsrmRoute(cacheKey, routePoints);
			// Anti-race : si l'utilisateur a basculé pendant le fetch OSRM
			// (peut prendre 1-2 s côté serveur public), on abandonne.
			if (currentRouteVehicleId !== vehicleId) {
				console.log("[P4] race lost after OSRM fetch", { vehicleId });
				return;
			}
			console.log("[P4] OSRM result", {
				vehicleId,
				ok: !!osrmPath,
				points: osrmPath ? osrmPath.length : straight.length,
				usingFallback: !osrmPath,
			});
			const polyPoints = osrmPath ?? straight;
			// Style "OSM directions" : trait bleu épais avec halo blanc
			// dessous pour le rendre lisible sur fond de carte clair ET sombre.
			// Le halo est dessiné d'abord (couche inférieure du group).
			L.polyline(polyPoints, {
				color: '#ffffff',
				weight: 8,
				opacity: 0.8,
			}).addTo(group);
			L.polyline(polyPoints, {
				color: '#2563eb',
				weight: 5,
				opacity: 0.9,
				// Pointillés uniquement en fallback ligne droite — un tracé
				// routier réel mérite un trait plein.
				dashArray: osrmPath ? undefined : '6, 8',
			}).addTo(group);
			// Markers : 3 styles distincts pour origine, intermédiaire, terminus.
			// Origine = pin vert (style "départ", icône play). Terminus = pin
			// rouge (style "arrivée", icône drapeau). Intermédiaires = pastilles
			// orange numérotées (compact, ne surchargent pas la carte quand
			// il y a beaucoup d'arrêts).
			let intermediateIdx = 0;
			stopPoints.forEach((p) => {
				let html: string;
				let size: [number, number] = [22, 22];
				let anchor: [number, number] = [11, 11];
				let tooltipText: string;
				if (p.kind === "origin") {
					// Pin vert "départ", forme goutte type Google Maps avec
					// la pointe en bas (anchor [16,32] = pointe sur la coord).
					html = `
						<div style="position:relative;width:32px;height:32px;">
							<svg viewBox="0 0 32 32" width="32" height="32" style="filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));">
								<path d="M16 0 C7.7 0 1 6.7 1 15 c0 11 15 17 15 17 s15-6 15-17 C31 6.7 24.3 0 16 0z" fill="#16a34a" stroke="#fff" stroke-width="2"/>
								<polygon points="11,9 23,15 11,21" fill="#fff"/>
							</svg>
						</div>`;
					size = [32, 32];
					anchor = [16, 32];
					tooltipText = `Départ · ${p.label}`;
				} else if (p.kind === "terminus") {
					// Pin rouge "arrivée", forme goutte avec drapeau à damier.
					html = `
						<div style="position:relative;width:32px;height:32px;">
							<svg viewBox="0 0 32 32" width="32" height="32" style="filter: drop-shadow(0 2px 3px rgba(0,0,0,0.4));">
								<path d="M16 0 C7.7 0 1 6.7 1 15 c0 11 15 17 15 17 s15-6 15-17 C31 6.7 24.3 0 16 0z" fill="#dc2626" stroke="#fff" stroke-width="2"/>
								<rect x="11" y="9" width="3" height="13" fill="#fff"/>
								<polygon points="14,9 22,12 14,15" fill="#fff"/>
							</svg>
						</div>`;
					size = [32, 32];
					anchor = [16, 32];
					tooltipText = `Arrivée · ${p.label}`;
				} else {
					intermediateIdx += 1;
					html = `
						<div style="background:#f97316;color:#fff;border:2px solid #fff;border-radius:50%;
									width:22px;height:22px;display:flex;align-items:center;justify-content:center;
									font-size:11px;font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,0.5);">
							${intermediateIdx}
						</div>`;
					tooltipText = `#${intermediateIdx} · ${p.label}`;
				}
				const icon = L.divIcon({
					html,
					className: "planned-stop-marker",
					iconSize: size,
					iconAnchor: anchor,
				});
				const m = L.marker([p.lat, p.lng], { icon });
				m.bindTooltip(tooltipText, { direction: "top", offset: [0, -anchor[1] + 4] });
				m.addTo(group);
			});
			group.addTo(map);
			plannedRouteLayer = group;
			console.log("[P4] polyline added to map", { vehicleId, pts: polyPoints.length });
			// Zoom auto sur l'étendue du trajet planifié + le marker du
			// véhicule s'il existe. Sans ce fit, la carte reste au zoom
			// initial centré sur la dernière position GPS (souvent zoom
			// niveau rue) et l'opérateur ne voit pas l'ensemble du tracé.
			//
			// On compose à partir des géométries explicites (polyPoints +
			// éventuelle position véhicule) plutôt que d'un featureGroup
			// imbriqué — Leaflet ne sait pas extraire les bounds d'un
			// LayerGroup enfant, ce qui rendait le fitBounds no-op auparavant.
			try {
				const allLatLngs: L.LatLngExpression[] = [...polyPoints];
				// On n'inclut le marker véhicule dans le fit QUE si la position
				// est dans la zone géographique du trajet (cf vehicleInTracé
				// plus haut). Sinon — typiquement test GPS sur un autre
				// continent — le fitBounds zoomait à l'échelle mondiale et
				// le tracé Cameroun devenait illisible.
				if (vehicleInTracé && vehicleMarker) {
					const ll = vehicleMarker.getLatLng();
					allLatLngs.push([ll.lat, ll.lng]);
				}
				stopPoints.forEach((p) => allLatLngs.push([p.lat, p.lng]));
				if (allLatLngs.length > 0) {
					const bounds = L.latLngBounds(allLatLngs);
					if (bounds.isValid()) map.fitBounds(bounds.pad(0.15));
				}
			} catch (e) {
				console.warn('[P4] fitBounds après tracé planifié', e);
			}
		} catch (e) {
			console.warn('[P4] Failed to load planned route for vehicle', vehicleId, e);
		}
		console.log("[P4] drawPlannedRouteForVehicle exit", { vehicleId });
	};

	// Mémorise les vehicleId pour lesquels on a déjà tenté un auto-draw
	// de tracé planifié au boot. Sans cette mémo, un fetch périodique
	// (polling 60s) re-déclencherait le draw à chaque tick et écraserait
	// l'éventuel zoom/scroll manuel de l'opérateur.
	const autoDrawAttempted = new Set<number>();

	const fetchFleet = async () => {
		try {
			const r: any = await api().vehiclePosition.fleet();
			const rows = (r?.data || []).filter((v: any) => v.lat != null && v.lng != null);
			console.log("[P4] fetchFleet rows", {
				count: rows.length,
				sample: rows.slice(0, 1).map((v: any) => ({
					id: v.id,
					vehicle_id: v.vehicle_id,
					travel_route_uuid: v.travel_route_uuid,
				})),
			});
			rows.forEach(upsertMarker);
			// Amorce la trace déjà parcourue pour chaque véhicule en
			// service : sans ça, ouvrir la carte sur un trajet commencé il
			// y a deux heures affichait un marqueur sans aucun chemin.
			rows.forEach((v: any) => {
				const vid = v.vehicle_id ?? v.id;
				if (vid && v.embarkation_id) seedLiveTrail(vid, v.embarkation_id);
			});
			// Si AU MOINS un véhicule a un travel_route associé, son auto-draw
			// va se déclencher et va appeler fitBounds sur le tracé planifié
			// (qui inclut soit le véhicule s'il est dans la zone, soit
			// uniquement les stops). On évite le fitToMarkers ici parce qu'il
			// flasherait sur la position GPS du véhicule (potentiellement
			// très loin si test/stale) avant que le tracé Cameroun ne prenne
			// le relais — UX déroutante.
			const willAutoDraw = rows.some((v: any) => {
				const id = v.vehicle_id ?? v.id;
				return id && !autoDrawAttempted.has(id) && v.travel_route_uuid;
			});
			if (!willAutoDraw && rows.length > 0) fitToMarkers();
			// Auto-draw du tracé planifié pour les véhicules actifs qui ont
			// un travel_route et qu'on n'a pas encore traité. Cas fréquent :
			// un seul véhicule en service → l'opérateur n'a pas besoin de
			// cliquer pour voir le trajet, il apparaît directement.
			for (const v of rows) {
				const id = v.vehicle_id ?? v.id;
				if (!id || autoDrawAttempted.has(id)) continue;
				autoDrawAttempted.add(id);
				// Lance en arrière-plan, ne bloque pas fetchFleet.
				// Si plusieurs véhicules, seul le DERNIER tracé reste
				// (overlay unique) — comportement intentionnel : l'opérateur
				// peut switcher en cliquant un autre véhicule.
				drawPlannedRouteForVehicle(id);
			}
		} catch (e) { console.warn('[fleet] fetch failed', e); }
	};

	// ── Trace GPS réellement parcourue (temps réel) ─────────────────────
	//
	// Distincte de l'overlay « trajet planifié » (drawPlannedRouteForVehicle,
	// qui dessine les arrêts prévus) : ici c'est le chemin effectif du
	// véhicule. Les deux superposés permettent de voir d'un coup d'œil si
	// le bus suit son itinéraire.
	//
	// Une polyline par véhicule, mémorisée pour pouvoir l'allonger sans
	// tout redessiner à chaque ping.
	const liveTrails = new Map<number, any>();
	// Borne mémoire : au-delà, on oublie les points les plus anciens. Une
	// journée de conduite à 10 s d'intervalle ferait ~8 600 points par
	// véhicule, ce qui finit par ramer sur un poste de guichet modeste.
	const appendToLiveTrail = (vehicleId: number, lat: number, lng: number) => {
		if (!map || vehicleId == null || lat == null || lng == null) return;
		let line = liveTrails.get(vehicleId);
		if (!line) {
			line = L.polyline([], {
				color: '#25e0d0',
				weight: 3,
				opacity: 0.85,
				// Pointillés : distingue visuellement la trace réelle de la
				// polyline pleine de l'itinéraire planifié.
				dashArray: '6 6',
			}).addTo(map);
			liveTrails.set(vehicleId, line);
		}
		line.addLatLng([lat, lng]);
		const pts = line.getLatLngs();
		if (pts.length > MAX_TRAIL_POINTS) {
			line.setLatLngs(pts.slice(pts.length - MAX_TRAIL_POINTS));
		}
	};

	/**
	 * Amorce la trace d'un véhicule avec ce qui a déjà été parcouru, pour
	 * que l'ouverture de la carte ne montre pas une trace vide sur un
	 * trajet commencé il y a deux heures. Les pings suivants l'allongent.
	 */
	const seedLiveTrail = async (vehicleId: number, embarkationId: number) => {
		if (!embarkationId || liveTrails.has(vehicleId)) return;
		try {
			const r: any = await api().vehiclePosition.trail(embarkationId);
			const pts = r?.data?.points ?? [];
			if (!pts.length) return;
			pts.forEach((p: any) => appendToLiveTrail(vehicleId, p.lat, p.lng));
		} catch (e) {
			// Amorçage best-effort : sans historique la carte reste
			// utilisable, la trace se construira à partir de maintenant.
			console.warn('[fleet] trail seed failed', e);
		}
	};

	const clearLiveTrails = () => {
		liveTrails.forEach((line) => line.remove());
		liveTrails.clear();
	};

	const subscribeEcho = () => {
		const eid = enterpriseID.value;
		if (!eid) return;
		try {
			echoChannel = getEcho().private(`enterprise.${eid}.fleet`);
			echoChannel.listen('.vehicle.position', (payload: any) => {
				upsertMarker({
					vehicle_id: payload.vehicle_id,
					matriculation: payload.matriculation,
					lat: payload.lat,
					lng: payload.lng,
					speed: payload.speed,
					heading: payload.heading,
					last_position_at: payload.recorded_at,
				});
				// La trace RÉELLEMENT parcourue s'allonge au fil des pings.
				// Sans elle, on ne voyait qu'un marqueur qui saute d'un
				// point à l'autre : impossible de dire par où le bus est
				// passé, ni s'il est sur son itinéraire.
				appendToLiveTrail(payload.vehicle_id, payload.lat, payload.lng);
			});
		} catch (e) { console.warn('[fleet] echo subscribe failed', e); }
	};

	watch(() => props.focusVehicleId, (id) => {
		if (id == null || !map) return;
		const m = markers.get(id);
		if (m) {
			map.setView(m.getLatLng(), Math.max(map.getZoom(), 14));
			m.openPopup();
		}
		// P4 — sur focus, on superpose le trajet planifié de ce véhicule.
		// Si l'utilisateur clique un autre véhicule, l'overlay précédent est
		// remplacé. drawPlannedRouteForVehicle est idempotent et silencieux
		// si aucun travel_route n'est associé.
		drawPlannedRouteForVehicle(id);
	});

	onMounted(async () => {
		if (!mapEl.value) return;
		// Default view roughly on Cameroun until first fetch
		map = L.map(mapEl.value, { zoomControl: true, attributionControl: true })
			.setView([4.05, 11.51], 6);

		L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
			maxZoom: 19,
			attribution: '&copy; OpenStreetMap',
		}).addTo(map);

		await fetchFleet();
		subscribeEcho();
	});

	onBeforeUnmount(() => {
		if (echoChannel) {
			try { echoChannel.stopListening('.vehicle.position'); } catch (e) { console.warn(e); }
		}
		markers.forEach((m) => m.remove());
		markers.clear();
		clearPlannedRoute();
		clearLiveTrails();
		if (map) { map.remove(); map = null; }
	});

	__expose({ fitToMarkers, refresh: fetchFleet, clearPlannedRoute, clearLiveTrails });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "mapEl",
    ref: mapEl,
    class: "w-full h-full min-h-[400px] rounded bg-gray-900"
  }, null, 512))
}
}

})